import { useEffect, useRef, useState } from 'react';

/**
 * Calcula bearing (graus 0-360, 0=Norte) entre dois pontos lat/lng.
 * Usado para girar o ícone do entregador na direção do movimento.
 */
function computeBearing(from, to) {
  if (!from || !to) return null;
  const [lat1, lng1] = from;
  const [lat2, lng2] = to;
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
}

/**
 * useAnimatedPosition
 * Interpola posições GPS [lat, lng] suavemente via requestAnimationFrame com easing
 * easeInOutCubic ("estilo Uber"). Também devolve o bearing em graus para que o ícone
 * do entregador possa ser rotacionado apontando na direção do movimento.
 *
 * @param {Array|null} target  [lat, lng] alvo (do WebSocket/polling)
 * @param {number} durationMs  Duração da interpolação (default 1200ms)
 * @returns {{ position: Array|null, bearing: number }}
 */
export default function useAnimatedPosition(target, durationMs = 1200) {
  const [current, setCurrent] = useState(target);
  const [bearing, setBearing] = useState(0);
  const fromRef = useRef(target);
  const toRef = useRef(target);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!target) {
      setCurrent(null);
      fromRef.current = null;
      toRef.current = null;
      return;
    }

    // Primeira posição: define direto, sem animar nem girar
    if (!current || !fromRef.current) {
      setCurrent(target);
      fromRef.current = target;
      toRef.current = target;
      return;
    }

    const [tlat, tlng] = target;
    const [clat, clng] = toRef.current || current;
    // Movimento muito pequeno (< ~1m) → não anima, não muda bearing
    if (Math.abs(tlat - clat) < 1e-6 && Math.abs(tlng - clng) < 1e-6) {
      return;
    }

    // Atualiza bearing baseado em (posição renderizada → novo alvo)
    const newBearing = computeBearing(current, target);
    if (newBearing !== null && !Number.isNaN(newBearing)) {
      setBearing(newBearing);
    }

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    fromRef.current = current;
    toRef.current = target;
    startRef.current = null;

    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const tick = (timestamp) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(t);
      const [flat, flng] = fromRef.current;
      const [nlat, nlng] = toRef.current;
      setCurrent([flat + (nlat - flat) * eased, flng + (nlng - flng) * eased]);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target ? target[0] : null, target ? target[1] : null, durationMs]);

  return { position: current, bearing };
}
